import {
  conditionProducts,
  makeShippingRate,
  numericConditionTypes,
  shippingConditionType,
  type OfferProduct,
  type ShippingCondition,
  type ShippingConditionType,
  type ShippingRate,
} from '@cod/config-schema';
import {
  Box,
  Button,
  Card,
  Icon,
  IconButton,
  Chip,
  // Label,       — country gating only
  // Select,      — country gating only
  Text,
  useToast,
} from '@nimbus-ds/components';
import {
  CheckIcon,
  EyeIcon,
  PencilIcon,
  PlusCircleIcon,
  PlusIcon,
  TrashIcon,
} from '@nimbus-ds/icons';
import { Page } from '@nimbus-ds/patterns';
import { useState } from 'react'; // useMemo — country gating only, see RateCountryGate below
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../components/states.js';
import { ProductPicker } from '../components/ProductPicker.js';
import {
  // CheckboxControl,  — country gating only, see RateCountryGate below
  NumberControl,
  SelectControl,
  TextControl,
} from '../components/controls.js';
import { useAppState } from '../state/AppState.js';
import { useUnsavedChanges } from '../state/NavigationGuard.js';
// import { countryOptions, selectedCountryOptions } from './countries.js';  — country gating only
import {
  canRemoveShippingRate,
  conditionPickerSelection,
  conditionProductPatch,
  removeShippingRate,
} from './shippingRates.model.js';

/**
 * 🚨 Per-rate COUNTRY GATING is disabled here — the "enable this rate only for specific
 * countries" checkbox and its country picker. The code is kept verbatim in `RateCountryGate`
 * below, which carries the full restore checklist; the schema fields and the runtime gate in
 * `rateEligible` are untouched-but-commented for the same reason. Do not delete either half
 * without the other, or a config saved with `country_only: true` starts hiding rates again with
 * no control to clear it.
 */

const isNumericType = (t: ShippingConditionType): boolean =>
  (numericConditionTypes as readonly string[]).includes(t);

/** One condition row inside a rate editor: a type select + a value input or a product picker. */
function ConditionRow({
  condition,
  onChange,
  onRemove,
}: {
  condition: ShippingCondition;
  onChange: (patch: Partial<ShippingCondition>) => void;
  onRemove: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [picking, setPicking] = useState(false);
  const numeric = isNumericType(condition.type);
  // Legacy single-product conditions read through the same helper the renderers use, so an old
  // rate opens showing its product rather than an empty button.
  const chosen = conditionProducts(condition);

  return (
    <Box display="flex" alignItems="flex-end" gap="2" flexWrap="wrap">
      <Box flex="1" minWidth="220px">
        <SelectControl<ShippingConditionType>
          id={`cond-type-${condition.type}-${condition.product_id ?? ''}`}
          label={t('shippingRates.conditionType')}
          value={condition.type}
          options={shippingConditionType.options.map((ct) => ({
            value: ct,
            label: t(`shippingRates.ct_${ct}`),
          }))}
          onChange={(type) =>
            // Reset the value/products when switching between numeric and product conditions.
            onChange(
              isNumericType(type)
                ? { type, products: [], product_id: undefined, product_name: undefined }
                : { type, value: 0 },
            )
          }
        />
      </Box>
      <Box flex="1" minWidth="220px">
        {numeric ? (
          <NumberControl
            id={`cond-value-${condition.type}`}
            label={t('shippingRates.conditionValue')}
            value={condition.value}
            onChange={(value) => onChange({ value })}
            max={100_000_000}
          />
        ) : (
          <Box display="flex" flexDirection="column" gap="1">
            <Text>{t('shippingRates.product')}</Text>
            {/* One name reads better than "1 product"; past that the names would wrap the button
                into a paragraph, so the count carries it and the chips below name them. */}
            <Button onClick={() => setPicking(true)}>
              {chosen.length === 0
                ? t('shippingRates.selectProduct')
                : chosen.length === 1
                  ? (chosen[0]?.name ?? String(chosen[0]?.id))
                  : t('shippingRates.productCount', { count: chosen.length })}
            </Button>
            {chosen.length > 1 && (
              <Box display="flex" flexWrap="wrap" gap="1">
                {chosen.map((p) => (
                  <Chip
                    key={p.id}
                    text={p.name || String(p.id)}
                    removable
                    onClick={() =>
                      onChange({
                        products: chosen.filter((x) => x.id !== p.id),
                        product_id: undefined,
                        product_name: undefined,
                      })
                    }
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
      <IconButton source={<TrashIcon />} onClick={onRemove} aria-label={t('common.delete')} />
      {!numeric && (
        <ProductPicker
          open={picking}
          initialSelected={conditionPickerSelection(condition)}
          // 🚨 Every checked product is kept. The picker is multi-select and used to be fed into a
          // single `product_id`, so a merchant who ticked three watched two of them disappear on
          // Add with nothing said — the condition is ANY-OF now, which is also the only way to
          // express "or" (conditions on a rate are ANDed).
          onAdd={(products: OfferProduct[]) => {
            onChange(conditionProductPatch(products));
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </Box>
  );
}

/** Inline editor for a single rate (expanded below its row). */
function RateEditor({
  rate,
  onChange,
}: {
  rate: ShippingRate;
  onChange: (patch: Partial<ShippingRate>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const setCondition = (i: number, patch: Partial<ShippingCondition>): void =>
    onChange({ conditions: rate.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const addCondition = (): void =>
    onChange({ conditions: [...rate.conditions, { type: 'order_total_gte', value: 0 }] });
  const removeCondition = (i: number): void =>
    onChange({ conditions: rate.conditions.filter((_, idx) => idx !== i) });

  return (
    <Box display="flex" flexDirection="column" gap="4" padding="4">
      <Box display="flex" gap="4" flexWrap="wrap">
        <Box flex="1" minWidth="220px">
          <TextControl
            id="rate-name"
            label={t('shippingRates.rateName')}
            value={rate.name}
            onChange={(name) => onChange({ name })}
          />
        </Box>
        <Box flex="1" minWidth="220px">
          <TextControl
            id="rate-desc"
            label={t('shippingRates.rateDescription')}
            value={rate.description}
            onChange={(description) => onChange({ description })}
          />
        </Box>
        <Box width="140px">
          <NumberControl
            id="rate-price"
            label={t('shippingRates.ratePrice')}
            value={rate.price}
            onChange={(price) => onChange({ price })}
            max={1_000_000}
          />
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap="2">
        <Text fontWeight="medium">{t('shippingRates.conditionsHeading')}</Text>
        {rate.conditions.map((c, i) => (
          <ConditionRow
            key={i}
            condition={c}
            onChange={(patch) => setCondition(i, patch)}
            onRemove={() => removeCondition(i)}
          />
        ))}
        <Box>
          <Button onClick={addCondition}>
            <Icon source={<PlusIcon />} color="currentColor" />
            {t('shippingRates.addCondition')}
          </Button>
        </Box>
      </Box>

      {/* COUNTRY GATING — disabled. The rate-level "only for these countries" control used to
          render here; it is kept verbatim in `RateCountryGate` below. See the file header. */}
    </Box>
  );
}

// ---------------------------------------------------------------------------------------------
// COUNTRY GATING — DISABLED (2026-09-08). Kept verbatim so it can be switched back on.
//
// The merchant could restrict a rate to a list of countries (`ShippingRate.country_only` +
// `countries`). Redundant for this app: a Nuvemshop store sells from one country and the COD
// form's own coverage already decides who can order, so every rate was offered to every buyer
// anyway — and the gate could only ever HIDE rates, silently, if the buyer country failed to
// resolve.
//
// To restore, in order:
//   1. uncomment the four import lines at the top of this file;
//   2. uncomment this component and render `<RateCountryGate rate={rate} onChange={onChange} />`
//      where the pointer comment sits in `RateEditor`;
//   3. uncomment the `countrySummary` line in `conditionSummary` below;
//   4. uncomment the `country_only` branch in `rateEligible`
//      (`packages/config-schema/src/shipping.logic.ts`) — without it the saved list is inert;
//   5. restore the two commented-out gate tests — `config-schema/src/shipping.logic.test.ts` and
//      `form-tree/src/shipping.test.ts` — and delete the "a stored country gate is INERT" test
//      that stands in for the first one.
//
// The schema keeps `country_only` / `countries` and the served config keeps `store_country`, so
// anything a merchant already saved survives the round trip and comes back with the UI.
// `pages/countries.ts` is left in place with its own test suite, so the option list this UI needs
// cannot rot while the UI is off.
//
// function RateCountryGate({
//   rate,
//   onChange,
// }: {
//   rate: ShippingRate;
//   onChange: (patch: Partial<ShippingRate>) => void;
// }): JSX.Element {
//   const { t, i18n } = useTranslation();
//   // Memoised because this component re-renders on every keystroke in the fields above; the
//   // lists themselves are cheap now, but rebuilding them per render churned the option elements
//   // for no reason. `countryOptions` is additionally cached per locale in `countries.ts`.
//   const options = useMemo(() => countryOptions(i18n.language), [i18n.language]);
//   const selected = useMemo(
//     () => selectedCountryOptions(rate.countries, i18n.language),
//     [rate.countries, i18n.language],
//   );
//   return (
//     <>
//       <CheckboxControl
//         name="rate-country-only"
//         label={t('shippingRates.countryOnly')}
//         checked={rate.country_only}
//         onChange={(country_only) => onChange({ country_only })}
//       />
//       {rate.country_only && (
//         <Box display="flex" flexDirection="column" gap="2">
//           <Box display="flex" flexDirection="column" gap="1">
//             <Label htmlFor="rate-countries">{t('shippingRates.countryCodes')}</Label>
//             {/* 🚨 An "add one" Select plus removable Chips — NOT Nimbus's `MultiSelect`.
//                 That component cannot be driven as a controlled input: it holds its selection in
//                 its own state, pushes `value` into it from one effect, and pushes that state back
//                 out through `onChange` from another. Feed its `onChange` into the `value` you give
//                 it and the two effects oscillate — the flicker on screen. It also fires
//                 `onChange([])` on mount, before the merchant touches anything, which is what wrote
//                 an empty list over a saved country and left the row reading "1 country(ies)" beside
//                 an empty box.
//                 This shape has no internal state at all: the config is the only source of truth. */}
//             <Select
//               id="rate-countries"
//               name="rate-countries"
//               value=""
//               onChange={(e) => {
//                 const code = (e.target as HTMLSelectElement).value;
//                 if (code) onChange({ countries: [...rate.countries, code] });
//               }}
//             >
//               <Select.Option value="" label={t('shippingRates.addCountry')} />
//               {options
//                 .filter((o) => !rate.countries.includes(o.value))
//                 .map((o) => (
//                   <Select.Option key={o.value} value={o.value} label={o.label} />
//                 ))}
//             </Select>
//           </Box>
//           {selected.length > 0 && (
//             <Box display="flex" flexWrap="wrap" gap="1">
//               {selected.map((o) => (
//                 <Chip
//                   key={o.value}
//                   text={o.label}
//                   removable
//                   onClick={() =>
//                     onChange({ countries: rate.countries.filter((c) => c !== o.value) })
//                   }
//                 />
//               ))}
//             </Box>
//           )}
//           <Text fontSize="caption" color="neutral-textLow">
//             {t('shippingRates.countryCodesHelp')}
//           </Text>
//         </Box>
//       )}
//     </>
//   );
// }
// ---------------------------------------------------------------------------------------------

/** A short, human summary of a rate's visibility for the list's Condition column. */
function conditionSummary(
  rate: ShippingRate,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  const parts: string[] = [];
  if (rate.conditions.length > 0) {
    parts.push(t('shippingRates.conditionsSummary', { count: rate.conditions.length }));
  }
  // COUNTRY GATING — disabled, see RateCountryGate above.
  // if (rate.country_only)
  //   parts.push(t('shippingRates.countrySummary', { count: rate.countries.length }));
  return parts.join(' · ');
}

/**
 * Shipping Rates page: manage the merchant's selectable shipping rates (create / edit / delete)
 * with optional conditions. Edits are staged and persisted on Save (read-modify-write scoped to
 * the shipping_rates slice), mirroring the Quantity Offers page.
 */
export function ShippingRates(): JSX.Element | null {
  const { t } = useTranslation();
  const { config, setConfig, saveShippingRates } = useAppState();
  const { addToast } = useToast();
  const [draft, setDraft] = useState<ShippingRate[] | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Registered above the `!config` bail-out so the hook order never changes. Staged rates are
  // held only here, so navigating away used to drop them silently.
  const dirty = draft !== null;
  useUnsavedChanges(dirty, () => {
    setDraft(null);
    setExpandedId(null);
  });

  if (!config) return null;

  const saved = config.shipping_rates;
  const rates = draft ?? saved;

  const stage = (next: ShippingRate[]): void =>
    setDraft(JSON.stringify(next) === JSON.stringify(saved) ? null : next);
  const update = (id: string, patch: Partial<ShippingRate>): void =>
    stage(rates.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRate = (): void => {
    const r = makeShippingRate();
    stage([...rates, r]);
    setExpandedId(r.id);
  };
  // The trash icon is hidden on the last rate, but the guard lives in the model so any other
  // route into deletion is a no-op too. `removeShippingRate` returns the same array when it
  // declined, which is the signal to leave the draft (and the expanded row) alone.
  const canRemove = canRemoveShippingRate(rates);
  const remove = (id: string): void => {
    const next = removeShippingRate(rates, id);
    if (next === rates) return;
    stage(next);
    if (expandedId === id) setExpandedId(null);
  };
  const saveChanges = async (): Promise<void> => {
    if (!dirty) return;
    setSaving(true);
    setConfig((prev) => ({ ...prev, shipping_rates: rates }));
    const ok = await saveShippingRates(rates);
    setSaving(false);
    addToast({
      id: 'sr-save',
      type: ok ? 'success' : 'danger',
      text: ok ? t('common.saved') : t('common.saveError'),
    });
    if (ok) setDraft(null);
  };
  const discard = (): void => {
    setDraft(null);
    setExpandedId(null);
  };

  return (
    <Page maxWidth="1000px">
      <Page.Body>
        {/* Actions at the top of the page (no page title). Save/Discard sit to the LEFT of Add
            rate and just fade in/out, so Add rate stays flush with the right edge of the table. */}
        <Box display="flex" justifyContent="flex-end" alignItems="center" gap="2" marginBottom="4">
          <div
            style={{
              display: 'flex',
              gap: 8,
              opacity: dirty ? 1 : 0,
              pointerEvents: dirty ? 'auto' : 'none',
              transition: 'opacity .2s ease',
              whiteSpace: 'nowrap',
            }}
          >
            <Button onClick={discard} disabled={!dirty || saving}>
              {t('common.discard')}
            </Button>
            <Button
              appearance="primary"
              onClick={() => void saveChanges()}
              disabled={!dirty || saving}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
          {/* One primary action per page (design checklist): Save commits the merchant's work, so
              the Add button is neutral. */}
          <Button onClick={addRate}>
            <Icon source={<PlusIcon />} color="currentColor" />
            {t('shippingRates.addRate')}
          </Button>
        </Box>
        {/* Column headers. Hidden below `md`: the rows stack there, so a four-column header sits
            over nothing — and at 390px it was four truncated words. */}
        <Box
          display={{ xs: 'none', md: 'flex' }}
          alignItems="center"
          gap="2"
          paddingX="4"
          paddingY="2"
        >
          <Box flex="1">
            <Text fontWeight="medium">{t('shippingRates.colName')}</Text>
          </Box>
          <Box width="100px">
            <Text fontWeight="medium">{t('shippingRates.colPrice')}</Text>
          </Box>
          <Box flex="1">
            <Text fontWeight="medium">{t('shippingRates.colCondition')}</Text>
          </Box>
          <Box width="80px">
            <Text fontWeight="medium">{t('shippingRates.colActions')}</Text>
          </Box>
        </Box>

        {rates.length === 0 ? (
          <Card>
            <Card.Body>
              <EmptyState title={t('shippingRates.empty')} />
            </Card.Body>
          </Card>
        ) : (
          <Box display="flex" flexDirection="column" gap="2">
            {rates.map((rate) => {
              const open = expandedId === rate.id;
              const summary = conditionSummary(rate, t);
              return (
                <Card key={rate.id}>
                  {/* 🚨 Wraps below `md`. Four columns in 358px gave every one of them ~89px, so
                      "Envio gratis" broke across two lines and the condition summary was clipped
                      mid-word — reported from a phone. The name takes the whole first line, then
                      price and condition share the second, and the actions stay pinned right. */}
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="2"
                    flexWrap={{ xs: 'wrap', md: 'nowrap' }}
                  >
                    <Box flex={{ xs: '1 1 100%', md: '1' }} minWidth="0">
                      <Text>{rate.name}</Text>
                    </Box>
                    <Box width={{ xs: 'auto', md: '100px' }}>
                      <Text>{rate.price.toFixed(2)}</Text>
                    </Box>
                    <Box flex="1" display="flex" alignItems="center" gap="1" minWidth="0">
                      {summary ? (
                        <Text color="neutral-textLow">{summary}</Text>
                      ) : (
                        <>
                          <Icon source={<EyeIcon />} color="neutral-textLow" />
                          <Text color="neutral-textLow">{t('shippingRates.alwaysVisible')}</Text>
                        </>
                      )}
                    </Box>
                    <Box width="80px" display="flex" gap="1">
                      <IconButton
                        source={open ? <CheckIcon /> : <PencilIcon />}
                        onClick={() => setExpandedId(open ? null : rate.id)}
                        aria-label={open ? t('common.close') : t('shippingRates.edit')}
                      />
                      {/* Hidden, not disabled: the admin has no tooltip pattern, so a greyed-out
                          trash icon would be a dead control with nowhere to say why. The caption
                          under the list carries the reason instead. */}
                      {canRemove && (
                        <IconButton
                          source={<TrashIcon />}
                          onClick={() => remove(rate.id)}
                          aria-label={t('common.delete')}
                        />
                      )}
                    </Box>
                  </Box>
                  {open && <RateEditor rate={rate} onChange={(patch) => update(rate.id, patch)} />}
                </Card>
              );
            })}
          </Box>
        )}

        {/* Why the trash icon vanished on the only row. Rendered once under the list rather than
            per row, so it reads as a rule about the list and not as a per-rate error. */}
        {rates.length > 0 && !canRemove && (
          <Box marginTop="2" paddingX="4">
            <Text fontSize="caption" color="neutral-textLow">
              {t('shippingRates.lastRateHelp')}
            </Text>
          </Box>
        )}

        <Box marginTop="4">
          <Button onClick={addRate}>
            <PlusCircleIcon />
            {t('shippingRates.addRate')}
          </Button>
        </Box>
      </Page.Body>
    </Page>
  );
}
